import { useState, useEffect, createContext, useContext, useRef } from "react";

import WebMap from "@arcgis/core/WebMap";
import MapView from "@arcgis/core/views/MapView";
import Graphic from "@arcgis/core/Graphic";
import { mapId } from "../constants";
import BasemapSwitcher from "../components/map/BasemapSwitcher";
import { useWindowSize } from "../hooks/useWindowSize";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { AlignmentContext } from "./AlignmentContext";
import ParkContext from "./ParkContext";
import CityContext from "./CityContext";
import { CompareContext } from "./CompareContext";
import Search from "@arcgis/core/widgets/Search";
import { LegendOverlay } from "../components/LegendOverlay";
import Zoom from "@arcgis/core/widgets/Zoom.js";
import { trackEvent } from "../hooks/useAnalytics";

import {
	parkLayerId,
	parkServePlaceLayerId,
	parkServiceLayerId,
	dataFields,
} from "../constants";
import FeatureSet from "@arcgis/core/rest/support/FeatureSet";
import Color from "@arcgis/core/Color";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";

const MapViewContext = createContext<MapView | null>(null);
const MapCityContext = createContext<MapCityContextType>({
	mapCity: "",
	setMapCity: () => {},
});

type MapCityContextType = {
	mapCity: string;
	setMapCity: (city: string) => void;
};

export { MapViewContext, MapCityContext };

export function MapViewProvider(props: any) {
	const [view, setMapView] = useState<MapView>();
	const [mapCity, setMapCity] = useState<string>("");
	const mapCityRef = useRef(mapCity);
	const { compare, setCompare } = useContext(CompareContext);
	const { alignment, setAlignment } = useContext(AlignmentContext);
	const {
		clickedPark,
		setClickedPark,
		clickedParkServiceArea,
		setClickedParkServiceArea,
	} = useContext(ParkContext);
	const windowSize = useWindowSize();

	// Adds a search widget to the map
	const searchWidget = new Search({
		view: view,
		resultGraphicEnabled: true,
		popupEnabled: false,
		id: "address-search",
	});

	let parkServePlaceLayer: FeatureLayer;
	let parkLayer: FeatureLayer;
	let parkServiceLayer: FeatureLayer;

	// @ts-ignore
	const walkAreaSymbol: SimpleFillSymbol = {
		type: "simple-fill",
		color: new Color([221, 232, 128, 0.7]),
		outline: new SimpleLineSymbol({
			style: "dash",
			color: new Color([175, 189, 66]),
			width: 2,
		}),
	};

	const legendContainer = "legend-box";

	// useEffect(() => {
	// 	if (city) {
	// 		console.log("directly to a specific city");
	// 		console.log(city);
	// 		console.log(city.name);
	// 		setMapCity(city.name);
	// 	}
	// }, [city]);

	useEffect(() => {
		mapCityRef.current = mapCity;
	}, [mapCity]);

	useEffect(() => {
		const map = new WebMap({
			portalItem: {
				id: mapId,
			},
		});

		const mV = new MapView({
			map,
			ui: {
				components: ["attribution"],
			},
			constraints: {
				rotationEnabled: false,
				minZoom: 4,
			},
			center: [-100, 40], //-100, 40
			zoom: 4,
			spatialReference: {
				wkid: 3857,
			},
		});
		const zoom = new Zoom({
			view: mV,
		});

		// Creates a new graphics layer for the 10-minute walk areas that may be generated relative to drawn parks and/or trails
		const walkAreaGL = new GraphicsLayer({
			id: "walk-layer",
			title: "walk layer",
		});

		// Creates a new graphics layer for the drawn parks and/or trails
		const sketchGL = new GraphicsLayer({
			title: "Park and Trail Sketch",
			id: "sketch-layer",
		});

		// Adds the drawn parks/trails layer after the 10-minute walk layer has been added to the map, ensuring they will render in the correct order
		map.layers.on("after-add", (e) => {
			if (e.item.id === "walk-layer") {
				map.add(sketchGL);
			}
		});

		const graphicsSearchLayer = new GraphicsLayer({
			title: "Searched Address",
		});

		mV.when((view: MapView) => {
			mV.popup.collapseEnabled = false;

			mV.map.add(walkAreaGL);

			LegendOverlay(view, legendContainer);

			searchWidget.activeSource.outFields = ['*']

			const toggle = BasemapSwitcher(view);
			view.ui.add(toggle, "manual");
			view.ui.add(searchWidget, "manual");
			view.ui.add(zoom, "manual");

			mV.map.allLayers.forEach((l) => {
				if (l.title.toLowerCase().includes("urban heat")) {
					l.set("legendEnabled", false);
				}
			});

			//@ts-ignore
			parkLayer = mV.map.findLayerById(parkLayerId);
			parkLayer.outFields = ["*"];

			//@ts-ignore
			parkServePlaceLayer = view?.map.findLayerById(parkServePlaceLayerId);

			searchWidget.on("select-result", function (evt: any) {
				//console.log("address search");
				//console.log(evt.result.feature.attributes);

				view?.map.add(graphicsSearchLayer);
				graphicsSearchLayer.removeAll();
				let geom = evt.result.feature.geometry;

				let pointSearchGraphic = new Graphic({
					geometry: geom as __esri.Point,
					symbol: {
						// @ts-ignore
						type: "simple-marker",
						color: [226, 119, 40], // Orange
						outline: {
							color: [255, 255, 255], // White
							width: 1,
						},
					},
				});

				graphicsSearchLayer.add(pointSearchGraphic);

				let query = parkServePlaceLayer.createQuery();
				query.spatialRelationship = "intersects";
				query.geometry = geom;
				query.returnGeometry = false;
				parkServePlaceLayer.queryFeatures(query).then(function (results) {
					//trackSearchAddress(evt.result.feature.attributes.City);
					if (results.features.length > 0) {
						setCompare(results.features[0].attributes.ID);
					}

					let addr_city = evt.result.feature.attributes[dataFields["City"]]
					let addr_state = evt.result.feature.attributes[dataFields["RegionAbbr"]]
					let addr_city_state = `${addr_city}, ${addr_state}`
					trackEvent("address_search", addr_city_state);
					
				});
			});
			searchWidget.on("search-clear", function (evt: any) {
				view?.map.remove(graphicsSearchLayer);
				graphicsSearchLayer.removeAll();
			});
		}).catch((error: any) => {
			if (error) {
				console.log(error);
			}
			if (error.name.includes("webgl")) {
				console.log("no web-gl support");
			}
		});

		// Event handler for any time the user clicks on the web map. Searches for any applicable park areas.
		mV?.on("click", (event: any) => {
			// @ts-ignore
			parkServiceLayer = mV.map.findLayerById(parkServiceLayerId);
			const opts = {
				include: parkLayer,
			};

			mV.hitTest(event, opts).then((response) => {
				walkAreaGL.removeAll();
				if (response.results.length) {
					// @ts-ignore
					const parkGraphic = response.results[0].graphic;
					if (parkGraphic.attributes[dataFields["ParkID"]]) {
						let query = parkServiceLayer.createQuery();
						query.where = `${dataFields["ParkID"]} = '${parkGraphic.attributes[dataFields["ParkID"]]}'`;
						// @ts-ignore
						query.outSpatialReference = {
							wkid: 3857,
						};
						query.outFields = ["*"];
						parkServiceLayer
							.queryFeatures(query)
							.then((serviceAreaResponse: FeatureSet) => {
								if (serviceAreaResponse.features.length) {
									const saGraphic = serviceAreaResponse.features[0].clone();
									saGraphic.symbol = walkAreaSymbol;
									walkAreaGL.add(saGraphic);
									setClickedPark?.(parkGraphic);
									//console.log(saGraphic.attributes);

									//console.log("Current mapCity:", mapCityRef.current);
									trackEvent("park_click", {
										park_click_name: saGraphic.attributes[dataFields["Park_Name"]],
										park_click_geography: mapCityRef.current,
									});
									setAlignment("park");
									setClickedParkServiceArea?.(saGraphic);
									walkAreaGL.visible = true;
								}
							});
					}
				}
			});
		});

		setMapView(mV);
	}, []);

	return (
		<MapCityContext.Provider value={{ mapCity, setMapCity }}>
			<MapViewContext.Provider
				value={view}
				{...props}
			></MapViewContext.Provider>
		</MapCityContext.Provider>
	);
}
